import { IAllowedFile } from 'models/allowedFiles.model'
import { ErrorToast } from 'components/ui/toast/toastsTemplates'
import I18n from 'i18n'
import { EvaluationCreationErrorsEnum } from 'enum/evaluationCreationErrors.enum'
import { QuestionOptionForm } from 'models/question.model'
import { IEvaluationQuestion } from 'models/evaluation.model'
import {
  BlockSurveyContent,
  BlockTaskContent,
} from 'store/states/lessons.state'
import { BlockTypes } from 'enum/block-type.enum'
import { QuestionTypeEnum } from 'enum'

export const lessonValidatorByRef = (
  refArray: React.MutableRefObject<(HTMLElement | null)[]>
) => {
  const emptyMandatories: HTMLElement[] = []
  refArray &&
    refArray.current.map((item: any) => {
      if (item)
        switch (item.id) {
          case 'likert': {
            let checked = false
            for (let x = 0; x < item.children[1].children.length; x++) {
              if (item.children[1].children[x].children[0].checked) {
                checked = true
                break
              }
            }
            if (checked === false) {
              emptyMandatories.push(item.children[1].children[0].children[0])
              item.lastChild.lastChild.lastChild.classList.add('d-flex')
            } else {
              item.lastChild.lastChild.lastChild.classList.remove('d-flex')
            }
            break
          }
          case 'openEnded':
            if (!item.children[0].children[1].textContent) {
              emptyMandatories.push(item.children[0].children[1])
              item.children[1].children[0].lastChild.classList.add('d-flex')
              item.children[0].children[1].classList.add('error')
            } else {
              item.children[1].children[0].lastChild.classList.remove('d-flex')
              item.children[0].children[1].classList.remove('error')
            }
            break
          case 'task':
            if (!item.children[1].children[0]) {
              emptyMandatories.push(item.children[1])
              item.children[2].children[0].lastChild.classList.add('d-flex')
            } else if (item.children[2]) {
              item.children[2].children[0].lastChild.classList.remove('d-flex')
            }
            break
          case 'evaluation':
            if (
              item.children[1].children[0].children[0].classList.contains('new')
            ) {
              emptyMandatories.push(item.children[1])
              item.lastChild.children[0].lastChild.classList.add('d-flex')
            }
            break
          default:
            break
        }
    })
  emptyMandatories.length > 0 && emptyMandatories[0].focus()

  return emptyMandatories
}

export const evaluationHasResponse = (data: IEvaluationQuestion) =>
  data.results?.attemps > 0

export const taskHasResponse = ({ response }: BlockTaskContent) =>
  (response?.attachments.length ?? 0) > 0

export const surveyHasResponse = (data: BlockSurveyContent) => {
  return data.questionsForms.every((question) => {
    if (!question.mandatory) return true
    switch (question.questionableType) {
      case QuestionTypeEnum.likert:
        return !!question.answer?.id
      case QuestionTypeEnum.openEnded:
        return !!question.answer?.content
    }
  })
}

export const blockHasResponse = (
  data: IEvaluationQuestion | BlockTaskContent | BlockSurveyContent
) => {
  switch (data.type) {
    case BlockTypes.BlockEvaluation:
      return evaluationHasResponse(data)
    case BlockTypes.BlockTask:
      return taskHasResponse(data)
    case BlockTypes.BlockSurvey:
      return surveyHasResponse(data)
  }
}

export const getExtensionOnly = (name: string) => {
  return (name.match(/\.([^.]*?)(?=\?|#|$)/) || [])[1]
}

export const awesomeFileInputValidator = (
  files: any,
  allowedFiles: IAllowedFile[],
  sizeLimit: number,
  sizeInWords = '1GB',
  multiFile: boolean,
  arrayLimit = 0
) => {
  const result: File[] = []
  let sizeExceeded,
    unsupportedFormat,
    totalSizeExceeded = false
  let sizeAux = 0

  files.some((file: any) => {
    const extension = getExtensionOnly(file.name)
    if (
      allowedFiles.some((allowedFile) =>
        allowedFile.extension.includes(extension.toLowerCase())
      )
    ) {
      if (file.size <= sizeLimit) {
        sizeAux += file.size
        if (arrayLimit > 0) {
          if (sizeAux <= arrayLimit) {
            result.push(file)
          } else {
            totalSizeExceeded = true
          }
        } else {
          result.push(file)
        }
      } else {
        sizeExceeded = true
      }
    } else {
      unsupportedFormat = true
    }
    if (!multiFile) return 'ok'
  })
  totalSizeExceeded &&
    ErrorToast(
      I18n.t(`lessons.contentBlocks.taskBlock.toast.totalMaxSizeExceeded`)
    )
  sizeExceeded &&
    ErrorToast(
      I18n.t(`lessons.contentBlocks.taskBlock.toast.sizeExceeded`, {
        size: sizeInWords,
      })
    )
  unsupportedFormat &&
    ErrorToast(
      I18n.t(`lessons.contentBlocks.taskBlock.toast.unsupportedFormat`)
    )

  return result
}

export const errorMessage = (error: any, translation: string) => {
  return String(error).indexOf('422') > 0 ? translation : 'genericError'
}

export const arraysEqual = (a1: any[], a2: any[]) =>
  a1.length === a2.length &&
  a1.every((o: any, idx: any) => objectsEqual(o, a2[idx]))

export const objectsEqual = (o1: any, o2: any) =>
  Object.keys(o1).length === Object.keys(o2).length &&
  Object.keys(o1).every((p) => o1[p] === o2[p])

export const validateEvaluationContent = ({ text, options }: any) => {
  if (text.trim() === '') return EvaluationCreationErrorsEnum.name
  const result = options.some((option: QuestionOptionForm) => {
    if (option.text.trim() === '') return EvaluationCreationErrorsEnum.options
  })
  if (result) return EvaluationCreationErrorsEnum.options
  return EvaluationCreationErrorsEnum.none
}

export const validateEvaluationConfig = (
  { config: { approvalPoints } }: any,
  questions: any,
  list: any
) => {
  let sum = 0
  list.map(({ questionableId, points }: any) => {
    const exist = questions.find(({ id }: any) => id === questionableId)
    if (exist) {
      sum = sum + exist.weight
    } else {
      sum = sum + points
    }
  })

  if (approvalPoints > sum) return false
  return true
}
